import { useMemo, useState } from "react";
import QRCode from "qrcode";
import { reviewMessage } from "@joogoos/contracts";

/**
 * One link and one QR code to the business's own Google review form ([37 §1](../../../../docs/37-google-business-profile.md)).
 * Joogoos sends nothing here and holds no list of anybody.
 *
 * **One link for everyone, on purpose.** Google removes reviews that were rewarded and forbids asking only happy
 * customers, so there is no *how did we do?* step before the link, here or ever, and the message offers nothing.
 */
export function AskForReviews({ link, name }: { link: string | null; name: string }) {
  if (!link) {
    return (
      <p className="text-[13px] text-muted max-w-[62ch] leading-relaxed">
        Joogoos has no Google listing linked to this business, so it has no review link to give you. Add your town under
        Where to find you and Joogoos will look for it. A business with no listing of its own cannot collect Google reviews.
      </p>
    );
  }
  return <Asking link={link} name={name} />;
}

function Asking({ link, name }: { link: string; name: string }) {
  const [message, setMessage] = useState(() => reviewMessage(name, link));
  const [copied, setCopied] = useState<"link" | "message" | null>(null);
  const qr = useMemo(() => qrPath(link), [link]);
  const copy = (what: "link" | "message", text: string) => {
    void navigator.clipboard?.writeText(text).then(() => setCopied(what));
  };
  return (
    <div className="flex flex-col gap-4 max-w-[62ch]">
      <div className="flex flex-wrap items-start gap-5">
        {/* Inline, because the app's policy loads no `data:` image. */}
        <svg role="img" aria-label="QR code to your Google review form" viewBox={`0 0 ${qr.box} ${qr.box}`} width={160} height={160} className="rounded-lg border border-line bg-white">
          <path d={qr.d} fill="#000" />
        </svg>
        <div className="flex flex-col gap-2 min-w-[220px] flex-1">
          <p className="text-[13px] font-semibold">Your review link</p>
          <code className="text-[12px] break-all">{link}</code>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => copy("link", link)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white">{copied === "link" ? "Copied" : "Copy the link"}</button>
            <button onClick={() => download(qr)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-line-2 text-ink">Download the QR code</button>
          </div>
        </div>
      </div>
      <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
        A message to send
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="bg-page border border-line rounded-lg px-3 py-2 text-[13px] font-normal" />
      </label>
      <button onClick={() => copy("message", message)} className="self-start px-3 py-1.5 rounded-lg text-xs font-semibold border border-line-2 text-ink">{copied === "message" ? "Copied" : "Copy the message"}</button>
      <p className="text-[12px] text-muted leading-relaxed">
        Ask everyone, not only the customers you expect to be happy, and offer nothing for a review. Google removes reviews
        that were rewarded. Joogoos sends none of this for you.
      </p>
    </div>
  );
}

/** The code as one path with a quiet border, so it scans and prints sharp at any size. */
function qrPath(text: string): { box: number; d: string } {
  const { size, data } = QRCode.create(text, { errorCorrectionLevel: "M" }).modules;
  let d = "";
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (data[y * size + x]) d += `M${x + 4} ${y + 4}h1v1h-1z`;
  return { box: size + 8, d };
}

function download({ box, d }: { box: number; d: string }) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box} ${box}"><rect width="100%" height="100%" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  a.download = "google-review-qr.svg";
  a.click();
  URL.revokeObjectURL(a.href);
}
